<template>
  <div class="io-content" ref="ioContentRef">
    <!-- ═══════════════════════════════════════════════
         DOCUMENT HEADER
         ═══════════════════════════════════════════════ -->
    <header class="io-header">
      <h1 class="io-title">{{ data.metadata.title }}</h1>
      <p class="io-confidential-label">{{ data.metadata.subtitle }}</p>
      <p class="io-access-note">{{ data.metadata.accessNote }}</p>
    </header>

    <!-- ═══════════════════════════════════════════════
         INDEX / TABLE OF CONTENTS
         ═══════════════════════════════════════════════ -->
    <nav class="io-index" id="io-index" ref="ioIndexRef">
      <h2 class="io-index-title">INDEX</h2>
      <ol class="io-index-list">
        <li
          v-for="(entry, idx) in indexEntries"
          :key="'idx-' + idx"
          :class="['io-index-entry', 'io-index-' + entry.type]"
        >
          <template v-if="entry.type === 'part'">
            <a
              class="io-index-part-title"
              :href="'#' + entry.anchorId"
              @click.prevent="scrollToChapter(entry.anchorId)"
            >{{ entry.title }}</a>
          </template>
          <template v-else>
            <a
              class="io-index-chapter-link"
              :href="'#' + entry.anchorId"
              @click.prevent="scrollToChapter(entry.anchorId)"
            >
              <span class="io-index-chapter-number" v-if="entry.chapterNumber">{{ entry.chapterNumber }}.</span>
              <span class="io-index-chapter-title">{{ entry.displayTitle }}</span>
              <span class="io-index-dots"></span>
            </a>
          </template>
        </li>
      </ol>
    </nav>

    <!-- ═══════════════════════════════════════════════
         OPENING NOTES
         ═══════════════════════════════════════════════ -->
    <section class="io-opening-notes" id="io-opening-notes">
      <template v-for="(note, idx) in data.openingNotes" :key="'note-' + idx">
        <!-- Divider title card -->
        <div v-if="note.isDivider" class="io-divider">
          <h2 class="io-divider-title">{{ note.dividerTitle }}</h2>
          <p v-if="note.dividerSubtitle" class="io-divider-subtitle">{{ note.dividerSubtitle }}</p>
        </div>
        <!-- Seal separator -->
        <hr v-else-if="note.isSealSeparator" class="io-seal-separator" />
        <!-- Regular note -->
        <div
          v-else
          class="io-opening-note"
          :class="{ 'io-note-italic': note.richText && note.richText.includes('<em>'), 'io-note-energy-seal': note.isEnergySeal }"
        >
          <p
            v-if="note.richText"
            v-html="note.richText"
            :class="['io-note-text', note.alignment === 'center' ? 'text-center' : '']"
          ></p>
          <p
            v-else
            :class="['io-note-text', note.alignment === 'center' ? 'text-center' : '']"
          >{{ note.text }}</p>
        </div>
      </template>
    </section>

    <!-- ═══════════════════════════════════════════════
         CHAPTERS
         ═══════════════════════════════════════════════ -->
    <article
      v-for="(chapter, cIdx) in data.chapters"
      :key="'ch-' + cIdx"
      :id="chapter.id"
      class="io-chapter"
    >
      <!-- Leading dividers (rendered before chapter header) -->
      <template v-for="(para, pIdx) in chapter.paragraphs" :key="'cd-' + pIdx">
        <div v-if="para.isDivider && isLeadingDivider(chapter, pIdx)" :id="para.dividerId" class="io-divider">
          <h2 class="io-divider-title">{{ para.dividerTitle }}</h2>
          <p v-if="para.dividerSubtitle" class="io-divider-subtitle">{{ para.dividerSubtitle }}</p>
        </div>
      </template>

      <!-- Chapter Header -->
      <header class="io-chapter-header">
        <p v-if="chapter.chapterNumber" class="io-chapter-number">
          CHAPTER {{ chapter.chapterNumber }}
        </p>
        <h2 class="io-chapter-title" v-html="formatChapterTitle(chapter)"></h2>
      </header>

      <!-- Chapter paragraphs -->
      <div class="io-chapter-body">
        <template v-for="(para, pIdx) in chapter.paragraphs" :key="'cp-' + pIdx">
          <!-- Skip leading dividers (already rendered above header) -->
          <template v-if="para.isDivider && isLeadingDivider(chapter, pIdx)"></template>
          <!-- Non-leading divider title card -->
          <div v-else-if="para.isDivider" :id="para.dividerId" class="io-divider">
            <h2 class="io-divider-title">{{ para.dividerTitle }}</h2>
            <p v-if="para.dividerSubtitle" class="io-divider-subtitle">{{ para.dividerSubtitle }}</p>
          </div>
          <ul v-else-if="para.isList" class="io-list" :id="para.anchorId || undefined">
            <li
              v-if="para.richText"
              v-html="para.richText"
              :class="[para.alignment === 'center' ? 'text-center' : '']"
            ></li>
            <li
              v-else
              :class="[para.alignment === 'center' ? 'text-center' : '']"
            >{{ para.text }}</li>
          </ul>
          <div v-else :id="para.anchorId || undefined">
            <!-- Image before paragraph -->
            <div v-if="para.image && para.imagePosition === 'before'" class="io-inline-image io-inline-image--captioned">
              <img :src="para.image" :alt="para.imageAlt || ''" />
            </div>
            <p
              v-if="para.richText"
              v-html="para.richText"
              :class="[para.imagePosition === 'before' ? 'io-image-caption' : 'io-paragraph', para.alignment === 'center' ? 'text-center' : '']"
            ></p>
            <p
              v-else
              :class="[para.imagePosition === 'before' ? 'io-image-caption' : 'io-paragraph', para.alignment === 'center' ? 'text-center' : '']"
            >{{ para.text }}</p>
            <!-- Image after paragraph -->
            <div v-if="para.image && para.imagePosition === 'after'" class="io-inline-image">
              <img :src="para.image" :alt="para.imageAlt || ''" />
            </div>
          </div>
        </template>
      </div>

      <!-- Subsections -->
      <section
        v-for="(sub, sIdx) in chapter.subsections"
        :key="'sub-' + cIdx + '-' + sIdx"
        :id="sub.anchorId || undefined"
        class="io-subsection"
      >
        <component
          v-if="sub.title"
          :is="sub.headingLevel === 'h2' ? 'h3' : 'h4'"
          :class="['io-subsection-title', sub.alignment === 'center' ? 'text-center' : '']"
          v-html="formatSubsectionTitle(sub.title)"
        ></component>

        <template v-for="(para, pIdx) in sub.paragraphs" :key="'sp-' + pIdx">
          <!-- Divider title card -->
          <div v-if="para.isDivider" :id="para.dividerId" class="io-divider">
            <h2 class="io-divider-title">{{ para.dividerTitle }}</h2>
            <p v-if="para.dividerSubtitle" class="io-divider-subtitle">{{ para.dividerSubtitle }}</p>
          </div>
          <!-- Seal separator -->
          <hr v-else-if="para.isSealSeparator" class="io-seal-separator" />
          <ul v-else-if="para.isList" class="io-list" :id="para.anchorId || undefined">
            <li
              v-if="para.richText"
              v-html="para.richText"
              :class="[para.alignment === 'center' ? 'text-center' : '']"
            ></li>
            <li
              v-else
              :class="[para.alignment === 'center' ? 'text-center' : '']"
            >{{ para.text }}</li>
          </ul>
          <div v-else :id="para.anchorId || undefined" :class="{ 'io-energy-seal': para.isEnergySeal }">
            <!-- Image before paragraph -->
            <div v-if="para.image && para.imagePosition === 'before'" class="io-inline-image io-inline-image--captioned">
              <img :src="para.image" :alt="para.imageAlt || ''" />
            </div>
            <p
              v-if="para.richText"
              v-html="para.richText"
              :class="[para.imagePosition === 'before' ? 'io-image-caption' : 'io-paragraph', para.alignment === 'center' ? 'text-center' : '']"
            ></p>
            <p
              v-else
              :class="[para.imagePosition === 'before' ? 'io-image-caption' : 'io-paragraph', para.alignment === 'center' ? 'text-center' : '']"
            >{{ para.text }}</p>
            <!-- Image after paragraph -->
            <div v-if="para.image && para.imagePosition === 'after'" class="io-inline-image">
              <img :src="para.image" :alt="para.imageAlt || ''" />
            </div>
          </div>
        </template>
      </section>

      <!-- Milestone Images (Chapter 13) -->
      <div v-if="chapter.milestoneImages && chapter.milestoneImages.length" class="io-milestone-gallery">
        <div
          v-for="(ms, mIdx) in chapter.milestoneImages"
          :key="'ms-' + mIdx"
          class="io-milestone-image-wrapper"
        >
          <img :src="ms.image" :alt="ms.alt" class="io-milestone-image" />
          <p class="io-milestone-caption">{{ ms.alt }}</p>
        </div>
      </div>

      <!-- Investment Table Image (Chapter 17) -->
      <div v-if="chapter.investmentTable" class="io-investment-table">
        <img
          :src="chapter.investmentTable.image"
          :alt="chapter.investmentTable.alt"
          class="io-investment-table-image"
        />
      </div>
    </article>

  </div>

  <!-- Teleport nav elements outside the drawer's transform context -->
  <Teleport to="body">
    <!-- BACK TO INDEX (Fixed Button) -->
    <button
      v-show="showBackToIndex"
      class="io-back-to-index"
      @click="scrollToIndex"
      aria-label="Back to Index"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="18 15 12 9 6 15"></polyline>
      </svg>
      <span>Index</span>
    </button>

    <!-- CHAPTER NAVIGATION ARROWS -->
    <div v-show="showChapterNav" class="io-chapter-nav">
      <button
        class="io-chapter-nav-btn io-nav-prev"
        @click="navigatePrevChapter"
        :disabled="currentChapterIdx <= 0"
        aria-label="Previous Chapter"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="18 15 12 9 6 15"></polyline>
        </svg>
      </button>
      <button
        class="io-chapter-nav-btn io-nav-next"
        @click="navigateNextChapter"
        :disabled="currentChapterIdx >= data.chapters.length - 1"
        aria-label="Next Chapter"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
    </div>
  </Teleport>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'

export default {
  name: 'InvestorOverviewContent',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const ioContentRef = ref(null)
    const ioIndexRef = ref(null)
    const showBackToIndex = ref(false)
    const showChapterNav = ref(false)
    const currentChapterIdx = ref(0)

    // Build index entries with anchorIds linked to actual chapters
    const indexEntries = computed(() => {
      const entries = []
      let chapterSeqNum = 0

      // Opening Notes — first entry, no number, anchors to opening notes section
      entries.push({
        type: 'chapter',
        title: 'Opening Notes',
        displayTitle: 'Opening Notes',
        anchorId: 'io-opening-notes',
        chapterNumber: null
      })

      // Build a lookup: extract keyword from index title → find matching chapter object
      const chapters = props.data.chapters || []
      const findChapterByKeyword = (indexTitle) => {
        // Extract the main keyword before "—" (e.g. "Era Shift" from "Era Shift — The New Global Cycle")
        const keyword = indexTitle.split('—')[0].trim().toUpperCase()
        for (const ch of chapters) {
          if (ch.title && ch.title.toUpperCase().includes(keyword)) return ch
        }
        // Fallback: try first two words of keyword
        const words = keyword.split(/\s+/)
        const shortKey = words.slice(0, 2).join(' ')
        if (shortKey.length > 2) {
          for (const ch of chapters) {
            if (ch.title && ch.title.toUpperCase().includes(shortKey)) return ch
          }
        }
        return null
      }

      // For inline chapters (no chapter object), find the parent chapter that contains their content
      const findParentChapter = (indexTitle) => {
        const keyword = indexTitle.split('—')[0].trim().toUpperCase()
        for (const ch of chapters) {
          // Search in chapter paragraphs for inline "CHAPTER X\nTITLE" text
          for (const p of (ch.paragraphs || [])) {
            if (p.text && p.text.toUpperCase().includes(keyword)) return ch
          }
          // Search in subsections
          for (const sub of (ch.subsections || [])) {
            if (sub.title && sub.title.toUpperCase().includes(keyword)) return ch
            for (const p of (sub.paragraphs || [])) {
              if (p.text && p.text.toUpperCase().includes(keyword)) return ch
            }
          }
        }
        return null
      }

      for (let i = 0; i < props.data.index.length; i++) {
        const entry = props.data.index[i]
        if (entry.type === 'part') {
          // Find the divider with matching title across all chapters
          let partAnchorId = 'io-opening-notes' // fallback
          const entryUpper = entry.title.split('\n')[0].trim().toUpperCase()
          // Match helper: entry must start with divider title followed by non-letter or end
          const matchesDivider = (divTitle) => {
            const dt = divTitle.toUpperCase()
            if (!entryUpper.startsWith(dt)) return false
            if (entryUpper.length === dt.length) return true
            const next = entryUpper[dt.length]
            return !/[A-Z]/.test(next) // "PART I" must not match "PART II"
          }
          for (const ch of chapters) {
            for (const p of (ch.paragraphs || [])) {
              if (p.isDivider && p.dividerId && p.dividerTitle && matchesDivider(p.dividerTitle)) {
                partAnchorId = p.dividerId
                break
              }
            }
            if (partAnchorId !== 'io-opening-notes') break
            for (const sub of (ch.subsections || [])) {
              for (const p of (sub.paragraphs || [])) {
                if (p.isDivider && p.dividerId && p.dividerTitle && matchesDivider(p.dividerTitle)) {
                  partAnchorId = p.dividerId
                  break
                }
              }
              if (partAnchorId !== 'io-opening-notes') break
            }
            if (partAnchorId !== 'io-opening-notes') break
          }
          entries.push({ type: 'part', title: entry.title, anchorId: partAnchorId })
        } else {
          // Extract clean display title
          let displayTitle = entry.title
            .replace(/\s*\.{2,}\s*\d+/g, '')
            .replace(/\s*…+\s*\d+/g, '')
            .trim()

          // Appendix entries get no number; all others increment
          const isAppendix = /^Appendix\s/i.test(displayTitle)
          let chapterNumber = null
          if (!isAppendix) {
            chapterSeqNum++
            chapterNumber = chapterSeqNum
          }

          // Use explicit anchorId if provided in index data, otherwise find by keyword
          let anchorId = 'io-opening-notes'
          if (entry.anchorId) {
            anchorId = entry.anchorId
          } else {
            const matchedCh = findChapterByKeyword(entry.title)
            if (matchedCh) {
              anchorId = matchedCh.id
            } else {
              const parentCh = findParentChapter(entry.title)
              if (parentCh) anchorId = parentCh.id
            }
          }

          entries.push({
            type: 'chapter',
            title: entry.title,
            displayTitle,
            anchorId,
            chapterNumber
          })
        }
      }
      return entries
    })

    // Scroll tracking — detect which chapter is in view
    let scrollContainer = null
    let observer = null

    const setupScrollTracking = () => {
      // Use setTimeout to ensure the drawer DOM is fully rendered
      setTimeout(() => {
        // The scroll container is the BottomDrawer's .drawer-container
        scrollContainer = document.querySelector('.drawer-container')
        if (!scrollContainer) return

        // Show chapter nav arrows once we have a scroll container
        showChapterNav.value = true

        // Show/hide Back-to-Index based on scroll position past the index
        scrollContainer.addEventListener('scroll', handleScroll)

        // IntersectionObserver for current chapter
        const options = {
          root: scrollContainer,
          rootMargin: '-20% 0px -70% 0px',
          threshold: 0
        }
        observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const idx = props.data.chapters.findIndex(ch => ch.id === entry.target.id)
              if (idx !== -1) {
                currentChapterIdx.value = idx
              }
            }
          }
        }, options)

        // Observe all chapter elements
        props.data.chapters.forEach(ch => {
          const el = document.getElementById(ch.id)
          if (el) observer.observe(el)
        })
      }, 300)
    }

    const handleScroll = () => {
      if (!scrollContainer || !ioIndexRef.value) return
      const indexBottom = ioIndexRef.value.getBoundingClientRect().bottom
      showBackToIndex.value = indexBottom < 0
    }

    const scrollToChapter = (anchorId) => {
      const el = document.getElementById(anchorId)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }

    const scrollToIndex = () => {
      const el = document.getElementById('io-index')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }

    const navigatePrevChapter = () => {
      if (currentChapterIdx.value > 0) {
        currentChapterIdx.value--
        scrollToChapter(props.data.chapters[currentChapterIdx.value].id)
      }
    }

    const navigateNextChapter = () => {
      if (currentChapterIdx.value < props.data.chapters.length - 1) {
        currentChapterIdx.value++
        scrollToChapter(props.data.chapters[currentChapterIdx.value].id)
      }
    }

    // Format chapter title — split on \n to create line breaks
    const formatChapterTitle = (chapter) => {
      if (!chapter.subtitle) return ''
      // Split on double newline to separate tagline from intro notes
      const parts = chapter.subtitle.split('\n\n')
      const taglinePart = parts[0]
      const introPart = parts.length > 1 ? parts.slice(1).join('\n\n') : null

      let html = taglinePart
        .split('\n')
        .map((line, i) => {
          const trimmed = line.trim()
          if (!trimmed) return ''
          if (i === 0) return `<span class="io-title-main">${trimmed}</span>`
          return `<span class="io-title-sub">${trimmed}</span>`
        })
        .join('')

      if (introPart) {
        const introLines = introPart.split('\n').map(l => l.trim()).filter(Boolean)
        html += introLines.map(l => `<span class="io-title-intro">${l}</span>`).join('')
      }

      return html
    }

    // Check if a divider paragraph is a leading divider (before any non-divider content)
    const isLeadingDivider = (chapter, pIdx) => {
      // A divider is "leading" if all paragraphs before it are also dividers
      for (let i = 0; i < pIdx; i++) {
        if (!chapter.paragraphs[i].isDivider) return false
      }
      return true
    }

    const formatSubsectionTitle = (title) => {
      if (!title) return ''
      // If title has \n, split into multiple lines  
      return title.split('\n').map(l => l.trim()).filter(Boolean).join('<br>')
    }

    onMounted(() => {
      setupScrollTracking()
    })

    onBeforeUnmount(() => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
      if (observer) {
        observer.disconnect()
      }
      // Hide teleported elements immediately on unmount
      showBackToIndex.value = false
      showChapterNav.value = false
    })

    return {
      ioContentRef,
      ioIndexRef,
      showBackToIndex,
      showChapterNav,
      currentChapterIdx,
      indexEntries,
      scrollToChapter,
      scrollToIndex,
      navigatePrevChapter,
      navigateNextChapter,
      formatChapterTitle,
      formatSubsectionTitle,
      isLeadingDivider
    }
  }
}
</script>

<style scoped>
/* ═══════════════════════════════════════════════
   IO-SCOPED TYPOGRAPHY & LAYOUT
   ═══════════════════════════════════════════════ */
.io-content {
  --io-font-size: 19px;
  --io-line-height: 1.7;
  --io-max-width: 780px;
  --io-paragraph-spacing: 1.2em;
  --io-heading-spacing: 2.4em;
  --io-section-spacing: 3.6em;

  font-size: var(--io-font-size);
  line-height: var(--io-line-height);
  max-width: var(--io-max-width);
  margin: 0 auto;
  padding: var(--doubleSpace) 0;
  color: var(--text-main);
  position: relative;
}

/* ═══════════════════════════════════════════════
   DOCUMENT HEADER
   ═══════════════════════════════════════════════ */
.io-header {
  text-align: center;
  margin-bottom: var(--io-section-spacing);
  padding: var(--io-section-spacing) 0;
  border-bottom: 1px solid var(--grey-darkest);
}

.io-confidential-label {
  font-size: 0.65em;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--grey-dark);
  margin-bottom: var(--io-paragraph-spacing);
}

.io-title {
  font-size: 2.4em;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 0.3em;
  color: var(--text-main);
}

.io-version {
  font-size: 0.8em;
  color: var(--grey);
  margin-bottom: 0.4em;
}

.io-access-note {
  font-size: 0.75em;
  font-style: italic;
  color: var(--grey-dark);
}

/* ═══════════════════════════════════════════════
   PART / SECTION DIVIDER CARDS
   ═══════════════════════════════════════════════ */
.io-divider {
  text-align: center;
  padding: var(--io-section-spacing) 0;
  margin: var(--io-section-spacing) 0;
  border-top: 1px solid var(--grey-darkest);
}

.io-divider-title {
  font-size: 2em;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-main);
  margin-bottom: 0.3em;
}

.io-divider-subtitle {
  font-size: 0.75em;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--grey-dark);
  margin: 0;
}

/* ═══════════════════════════════════════════════
   OPENING NOTES
   ═══════════════════════════════════════════════ */
.io-opening-notes {
  margin-bottom: var(--io-section-spacing);
  padding-bottom: var(--io-section-spacing);
  border-bottom: 1px solid var(--grey-darkest);
}

.io-opening-note {
  margin-bottom: var(--io-paragraph-spacing);
}

.io-note-text {
  font-size: 0.9em;
  line-height: 1.8;
  color: var(--grey-lighter);
  white-space: pre-line;
}

.io-note-italic .io-note-text {
  font-style: italic;
  color: var(--grey);
}

.io-note-energy-seal .io-note-text {
  font-size: 0.8em;
}

.io-seal-separator {
  border: none;
  border-top: 1px solid var(--grey-darkest);
  width: 40%;
  margin: 2em auto;
}

.io-energy-seal .io-paragraph {
  font-size: 0.8em;
  font-style: italic;
  color: var(--grey);
}

/* ═══════════════════════════════════════════════
   INDEX / TABLE OF CONTENTS
   ═══════════════════════════════════════════════ */
.io-index {
  margin-bottom: var(--io-section-spacing);
  padding: var(--io-heading-spacing) 0;
  border-bottom: 1px solid var(--grey-darkest);
}

.io-index-title {
  font-size: 1.8em;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-align: center;
  margin-bottom: var(--io-heading-spacing);
  color: var(--text-main);
}

.io-index-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.io-index-entry {
  margin-bottom: 0.35em;
}

/* Part entries — bold, spaced section dividers */
.io-index-part {
  margin-top: var(--io-heading-spacing);
  margin-bottom: 0.6em;
}

.io-index-part-title {
  font-size: 0.85em;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-main);
  display: block;
  padding-top: 0.6em;
}

/* Chapter entries — clickable with dot leaders */
.io-index-chapter {
  padding-left: 1.2em;
}

.io-index-chapter-link {
  display: flex;
  align-items: baseline;
  text-decoration: none;
  color: var(--grey-lighter);
  font-size: 0.9em;
  line-height: 1.6;
  transition: color 0.2s ease;
  gap: 0.4em;
}

.io-index-chapter-link:hover {
  color: var(--text-main);
}

.io-index-chapter-number {
  flex-shrink: 0;
  color: var(--grey-dark);
  min-width: 2em;
}

.io-index-chapter-title {
  flex-shrink: 0;
  max-width: 85%;
}

.io-index-dots {
  flex: 1;
  border-bottom: 1px dotted var(--grey-darkest);
  min-width: 20px;
  margin-bottom: 0.25em;
}

/* ═══════════════════════════════════════════════
   CHAPTERS
   ═══════════════════════════════════════════════ */
.io-chapter {
  margin-bottom: var(--io-section-spacing);
  padding-top: var(--io-heading-spacing);
  scroll-margin-top: 40px;
}

.io-chapter-header {
  text-align: center;
  margin-bottom: var(--io-heading-spacing);
  padding-bottom: var(--io-paragraph-spacing);
}

.io-chapter-number {
  font-size: 0.7em;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--grey-dark);
  margin-bottom: 0.6em;
}

.io-chapter-title {
  font-size: 1.6em;
  font-weight: 600;
  letter-spacing: 0.06em;
  line-height: 1.4;
  color: var(--text-main);
}

.io-chapter-title :deep(.io-title-main) {
  display: block;
  text-transform: uppercase;
  font-size: 1em;
}

.io-chapter-title :deep(.io-title-sub) {
  display: block;
  font-size: 0.7em;
  color: var(--grey-lighter);
  margin-top: 0.15em;
}

.io-chapter-title :deep(.io-title-intro) {
  display: block;
  font-size: 0.55em;
  font-weight: 400;
  letter-spacing: 0;
  color: var(--grey-lighter);
  line-height: 1.8;
  margin-top: 0.8em;
}

/* ═══════════════════════════════════════════════
   PARAGRAPHS & BODY TEXT
   ═══════════════════════════════════════════════ */
.io-chapter-body,
.io-subsection {
  margin-bottom: var(--io-heading-spacing);
}

.io-paragraph {
  margin-bottom: var(--io-paragraph-spacing);
  white-space: pre-line;
  font-weight: 300;
  color: var(--grey-lighter);
}

.io-paragraph :deep(strong) {
  font-weight: 600;
  color: var(--text-main);
}

.io-paragraph :deep(em) {
  font-style: italic;
  color: var(--grey);
}

/* ═══════════════════════════════════════════════
   LISTS
   ═══════════════════════════════════════════════ */
.io-list {
  list-style: none;
  padding-left: 1.4em;
  margin-bottom: var(--io-paragraph-spacing);
}

.io-list li {
  position: relative;
  padding-left: 1em;
  margin-bottom: 0.5em;
  font-weight: 300;
  color: var(--grey-lighter);
  white-space: pre-line;
}

.io-list li::before {
  content: '—';
  position: absolute;
  left: 0;
  color: var(--grey-dark);
}

.io-list li :deep(strong) {
  font-weight: 600;
  color: var(--text-main);
}

/* ═══════════════════════════════════════════════
   SUBSECTIONS
   ═══════════════════════════════════════════════ */
.io-subsection-title {
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1.5;
  margin-bottom: var(--io-paragraph-spacing);
  margin-top: var(--io-heading-spacing);
  color: #fff;
  white-space: pre-line;
}

h3.io-subsection-title {
  font-size: 1.15em;
  padding-bottom: 0.4em;
  border-bottom: 1px solid var(--grey-darkestest);
}

h4.io-subsection-title {
  font-size: 1em;
  opacity: 1;
}

/* ═══════════════════════════════════════════════
   IMAGES — Milestones & Investment Table
   ═══════════════════════════════════════════════ */
.io-milestone-gallery {
  margin: var(--io-section-spacing) 0;
}

.io-milestone-image-wrapper {
  margin-bottom: var(--io-heading-spacing);
  text-align: center;
}

.io-milestone-image {
  width: 100%;
  max-width: 860px;
  height: auto;
  border-radius: var(--border-radius-small);
  border: 1px solid var(--grey-darkestest);
}

.io-milestone-caption,
.io-image-caption {
  font-size: 0.72em;
  color: var(--grey-dark);
  margin-top: 0.6em;
  letter-spacing: 0.06em;
}

.io-inline-image {
  margin: var(--io-paragraph-spacing) 0 var(--io-heading-spacing) 0;
  text-align: center;
}

.io-inline-image.io-inline-image--captioned {
  margin-bottom: 0;
}

.io-inline-image img {
  width: 100%;
  max-width: 860px;
  height: auto;
  border-radius: var(--border-radius-small);
  border: 1px solid var(--grey-darkestest);
}

.io-investment-table {
  margin: var(--io-heading-spacing) 0;
  text-align: center;
}

.io-investment-table-image {
  width: 100%;
  max-width: 860px;
  height: auto;
  border-radius: var(--border-radius-small);
  border: 1px solid var(--grey-darkestest);
}

/* ═══════════════════════════════════════════════
   UTILITY
   ═══════════════════════════════════════════════ */
.text-center {
  text-align: center;
}

/* ═══════════════════════════════════════════════
   RESPONSIVE: Laptop (max-width: 1200px)
   ═══════════════════════════════════════════════ */
@media screen and (max-width: 1200px) {
  .io-content {
    --io-font-size: 17.5px;
    --io-max-width: 720px;
  }
}

/* ═══════════════════════════════════════════════
   RESPONSIVE: Tablet (max-width: 768px)
   ═══════════════════════════════════════════════ */
@media screen and (max-width: 768px) {
  .io-content {
    --io-font-size: 16.5px;
    --io-max-width: 100%;
    padding: var(--space) 0;
  }

  .io-title {
    font-size: 2em;
  }

  .io-chapter-title {
    font-size: 1.35em;
  }
}

/* ═══════════════════════════════════════════════
   RESPONSIVE: Mobile (max-width: 590px)
   ═══════════════════════════════════════════════ */
@media screen and (max-width: 590px) {
  .io-content {
    --io-font-size: 16px;
    --io-line-height: 1.65;
    --io-paragraph-spacing: 1em;
    --io-heading-spacing: 1.8em;
    --io-section-spacing: 2.8em;
  }

  .io-title {
    font-size: 1.6em;
  }

  .io-header {
    padding: var(--io-heading-spacing) 0;
  }

  .io-index-chapter {
    padding-left: 0.6em;
  }

  .io-milestone-image {
    border-radius: 0;
  }
}
</style>

<!-- Non-scoped styles for Teleported elements (arrows & back-to-index) -->
<style>
/* ═══════════════════════════════════════════════
   BACK TO INDEX BUTTON (Fixed, teleported to body)
   ═══════════════════════════════════════════════ */
.io-back-to-index {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  height: 36px;
  border: 1px solid #424242;
  color: #bfbfbf;
  padding: 0 11px;
  border-radius: 48px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  transition: all 0.25s ease;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  background-color: rgba(16, 16, 16, 0.85);
  font-family: inherit;
}

.io-back-to-index:hover {
  color: #fff;
  border-color: #595959;
}

.io-back-to-index svg {
  width: 16px;
  height: 16px;
}

/* ═══════════════════════════════════════════════
   CHAPTER NAVIGATION ARROWS (Fixed, teleported to body)
   ═══════════════════════════════════════════════ */
.io-chapter-nav {
  position: fixed;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1100;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.io-chapter-nav-btn {
  background: rgba(16, 16, 16, 0.75);
  border: 1px solid #424242;
  color: #595959;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
  padding: 0;
}

.io-chapter-nav-btn:hover:not(:disabled) {
  color: #fff;
  border-color: #595959;
}

.io-chapter-nav-btn:disabled {
  opacity: 0.2;
  cursor: default;
}

.io-chapter-nav-btn svg {
  width: 18px;
  height: 18px;
}

@media screen and (max-width: 768px) {
  .io-chapter-nav {
    right: 10px;
  }

  .io-chapter-nav-btn {
    width: 32px;
    height: 32px;
  }
}

@media screen and (max-width: 590px) {
  .io-chapter-nav {
    display: none;
  }
}
</style>
